import { DateValueObject } from '@/helpers/dobValidation';
import { SemboDate } from '@libs/sembo-dates/semboDates';
import { DateFormat, Duration, PrecisionTypes } from '@shared/interfaces/dates';
import { t } from '@shared/helpers/translation';
import { LabelFormat } from '@shared/interfaces/translation';

const defaultDateFormat = DateFormat.DateReverse;

export function convertDateObjectToString(value: DateValueObject): string {
    //Output format: '2024-02-05' or '' if some param is missing
    if (!value.year || !value.month || !value.day) {
        return '';
    }
    const month = parseInt(value.month, 10);
    const day = parseInt(value.day, 10);
    const year = parseInt(value.year, 10);
    return `${year}-${month <= 9 ? `0${month}` : month}-${day <= 9 ? `0${day}` : day}`;
}
export function convertStringToDateObject(value: string | null): DateValueObject {
    //Output format: {year: '2024', month: '2', day: '5'}
    if (!value) {
        return {
            year: undefined,
            month: undefined,
            day: undefined,
        };
    }
    const parts = value.split('-');
    const year = parts[0] && parts[0].length ? parseInt(parts[0], 10) : '';
    const month = parts[1] && parts[1].length ? parseInt(parts[1], 10) : '';
    const day = parts[2] && parts[2].length ? parseInt(parts[2], 10) : '';
    return {
        year: year.toString(),
        month: month.toString(),
        day: day.toString(),
    };
}

export function getMonthAsNumber(value: Date): number {
    //Output format: Januari => 1, February => 2, ...
    return getMonth(value) + 1;
}

export function getTime(value: Date): string {
    //Output format: 00:00
    return new SemboDate(value).format(DateFormat.Time);
}

export function getDurationInHours(fromDate: Date, toDate: Date, positive = true): number {
    const diff = new SemboDate(fromDate).diff(toDate, 'hour');
    return positive ? Math.abs(diff) : diff;
}

export function getDurationInDays(fromDate: Date, toDate: Date, positive = true): number {
    const diff = new SemboDate(fromDate).diff(toDate, 'day');
    return positive ? Math.abs(diff) : diff;
}

export function getDifferenceInYears(fromDate: Date, toDate: Date, positive = true): number {
    const diff = new SemboDate(fromDate).diff(toDate, 'year');
    return positive ? Math.abs(diff) : diff;
}

export function getDifferenceInMillisecond(
    fromDate: Date,
    toDate: Date,
    positive = true
): number {
    const diff = new SemboDate(fromDate).diff(toDate, 'millisecond');
    return positive ? Math.abs(diff) : diff;
}

export function getDateAddingDuration(value: Date, duration: Duration): Date {
    // Returns the date adding the given duration from sembo-dates
    return new SemboDate(value).add(duration).get();
}

export function getDateSubtractingDuration(value: Date, duration: Duration): Date {
    // Returns the date subtracting the given duration from sembo-dates
    return new SemboDate(value).sub(duration).get();
}

export function getDateAvoidingZeroTimezone(value: string): Date {
    // Returns a new local date and avoiding the Zero timezone
    const dateString = value.replace(/z$/gim, '');
    return new SemboDate(dateString).get();
}

export function getShortDate(value: Date): string {
    // Output format: 2024-01-18
    return new SemboDate(value).format(defaultDateFormat);
}

export function getFormattedDate(value: Date, dateFormat?: DateFormat): string {
    // Returns the date formatted using the DateFormat provided by sembo-dates
    if (!dateFormat) dateFormat = defaultDateFormat;

    try {
        return new SemboDate(value).format(dateFormat);
    } catch {
        return new SemboDate(value).format(defaultDateFormat);
    }
}

function cleanDate(input: string): Date {
    // '/Date(1736546500000)/' => 1736546500000
    const ms = parseInt(input.replace(/\D/g, ''));
    return new Date(ms);
}

export function convertToDate(value?: Date | string): Date {
    // Default output: 2024-01-18

    if (typeof value === 'string' && value.toLowerCase().includes('date')) {
        return cleanDate(value);
    }
    return new SemboDate(value).get();
}

export function convertMillisecondsToMinutes(ms: number): number {
    return Math.ceil(ms / (1000 * 60));
}

export function convertMillisecondsToTime(ms: number) {
    const portions: string[] = [];

    const msInHour = 1000 * 60 * 60;
    const hours = Math.trunc(ms / msInHour);
    if (hours > 0) {
        portions.push(hours + 'h');
        ms = ms - hours * msInHour;
    }

    const msInMinute = 1000 * 60;
    const minutes = Math.trunc(ms / msInMinute);
    if (minutes > 0) {
        portions.push(minutes + 'm');
        ms = ms - minutes * msInMinute;
    }

    const seconds = Math.trunc(ms / 1000);
    if (seconds > 0) {
        portions.push(seconds + 's');
    }

    return portions.join(' ');
}

export function isValidDate(date?: Date | string | null): boolean {
    if (date === null || date === undefined) {
        return false;
    }

    try {
        return new SemboDate(date).isValidDate();
    } catch {
        return false;
    }
}

export function isValidTime(time?: string | null): boolean {
    if (!time || !/^\d{2}:\d{2}$/.test(time)) return false;

    const [hours, minutes] = time.split(':').map(Number);
    return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
}

export function isBefore(date: Date, dateToCompare: Date): boolean {
    return new SemboDate(date).isBefore(dateToCompare);
}

export function isAfter(date: Date, dateToCompare: Date): boolean {
    return new SemboDate(date).isAfter(dateToCompare);
}

export function isBetween(currentDate: Date, date1: Date, date2: Date): boolean {
    return new SemboDate(currentDate).isBetween(date1, date2);
}

export function isSameOrAfter(date: Date, dateToCompare: Date): boolean {
    const d = new SemboDate(date);
    return d.isSame(dateToCompare) || d.isAfter(dateToCompare);
}

export function isSameOrBefore(date: Date, dateToCompare: Date): boolean {
    const d = new SemboDate(date);
    return d.isSame(dateToCompare) || d.isBefore(dateToCompare);
}

export function isSameDate(date: Date, dateToCompare: Date): boolean {
    return new SemboDate(date).isSame(dateToCompare);
}

export function isSameDay(date: Date, dateToCompare: Date): boolean {
    return new SemboDate(date).isSameDay(dateToCompare);
}

export function isSameMonth(date1: Date, date2: Date): boolean {
    return new SemboDate(date1).isSameMonth(date2);
}

export function totalDaysInMonth(date: Date): number {
    return new SemboDate(date).getDaysInMonth();
}

export function getMonth(date: Date): number {
    return new SemboDate(date).getMonth();
}

export function getStartOf(date: Date, precision: PrecisionTypes): Date {
    return new SemboDate(date).startOf(precision).get();
}

export function getEndOf(date: Date, precision: PrecisionTypes): Date {
    return new SemboDate(date).endOf(precision).get();
}

export function getWeekDay(date: Date): number {
    return new SemboDate(date).getIsoDay();
}

export function setWeekDay(date: Date, weekDay: number): Date {
    const d = new SemboDate(date);
    d.setIsoDay(weekDay);
    return d.get();
}

export function today(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

export function formatMinutes(minutes: number): string | undefined {
    if (isNaN(minutes) || minutes < 0) {
        return undefined;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    const labelHour = t({ key: 'date.format.hour', format: LabelFormat.LowerCase });
    const labelMinutes = t({ key: 'date.format.minute', format: LabelFormat.LowerCase });

    const parts: string[] = [];

    if (hours > 0) {
        parts.push(`${hours}${labelHour}`);
    }

    if (remainingMinutes > 0 || parts.length === 0) {
        parts.push(`${remainingMinutes}${labelMinutes}`);
    }

    return parts.join(', ');
}

export function formatMinutesUnit(seconds: number): string | undefined {
    if (!Number.isFinite(seconds) || seconds < 0) {
        return undefined;
    }

    const totalMinutes = Math.ceil(seconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const remainingMinutes = totalMinutes % 60;

    const labelHour = t({
        key: 'date.unit.hour',
        keyPlural: 'date.unit.hours',
        format: LabelFormat.LowerCase,
        count: hours,
    });
    const labelMinutes = t({
        key: 'date.unit.minute',
        keyPlural: 'date.unit.minutes',
        format: LabelFormat.LowerCase,
        count: remainingMinutes,
    });

    const parts: string[] = [];

    if (hours > 0) {
        parts.push(`${hours} ${labelHour}`);
    }

    if (remainingMinutes > 0 || parts.length === 0) {
        parts.push(`${remainingMinutes} ${labelMinutes}`);
    }

    return parts.join(', ');
}

export function addTimeToDate(d: Date | string, time: string): string {
    const date = convertToDate(d);
    const dateTime = `${getShortDate(date)}T${time}:00`;
    return isValidDate(dateTime) ? dateTime : getFormattedDate(date);
}
